import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, KeyRound, Power, PowerOff, Loader2, Copy, Users, UserX, Ban, FileSpreadsheet, FileText, Download, Gift, TestTube, UserPlus, Clock, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const UserManagementPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tempPasswordDialog, setTempPasswordDialog] = useState({ open: false, username: '', password: '' });
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'inactive'
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    account_type: 'manual',
    access_days: '',
    force_password_change: true,
    notes: ''
  });

  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [currentUser, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const payload = {
        username: formData.username,
        email: formData.email,
        account_type: formData.account_type,
        force_password_change: formData.force_password_change
      };
      
      if (formData.password) {
        payload.password = formData.password;
      }
      if (formData.access_days && parseInt(formData.access_days) > 0) {
        payload.access_days = parseInt(formData.access_days);
      }
      if (formData.notes) {
        payload.notes = formData.notes;
      }
      
      const response = await axios.post(`${API}/admin/users`, payload);
      toast.success('Utilisateur créé avec succès');
      
      setTempPasswordDialog({
        open: true,
        username: response.data.username,
        password: response.data.temporary_password,
        account_type: response.data.account_type,
        expires_at: response.data.expires_at
      });
      
      fetchUsers();
      setDialogOpen(false);
      setFormData({ 
        username: '', 
        email: '', 
        password: '',
        account_type: 'manual',
        access_days: '',
        force_password_change: true,
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleResetPassword = async (username) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${username} ?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/users/${username}/reset-password`);
      toast.success('Mot de passe réinitialisé');
      
      setTempPasswordDialog({
        open: true,
        username: response.data.username,
        password: response.data.temporary_password
      });
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const handleToggleStatus = async (username) => {
    try {
      await axios.patch(`${API}/admin/users/${username}/toggle-status`);
      toast.success('Statut modifié');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Supprimer l'utilisateur ${username} ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${username}`);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papiers');
  };

  // Export functions
  const handleExportExcel = async () => {
    try {
      const statusParam = activeTab === 'active' ? 'active' : activeTab === 'inactive' ? 'inactive' : 'all';
      const response = await axios.get(`${API}/admin/users/export/excel?status_filter=${statusParam}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `utilisateurs_${statusParam}_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const statusParam = activeTab === 'active' ? 'active' : activeTab === 'inactive' ? 'inactive' : 'all';
      const response = await axios.get(`${API}/admin/users/export/pdf?status_filter=${statusParam}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `utilisateurs_${statusParam}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  // Filter users by status
  const activeUsers = users.filter(u => u.is_active);
  const inactiveUsers = users.filter(u => !u.is_active);
  const displayedUsers = activeTab === 'active' ? activeUsers : inactiveUsers;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto fade-in">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              onClick={() => navigate('/search')}
              variant="outline"
              className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Gestion des utilisateurs
              </h1>
              <p className="text-sm text-gray-400 mt-1">
                {activeUsers.length} actif(s) • {inactiveUsers.length} inactif(s)
              </p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn" className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Créer un utilisateur
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle>Créer un nouvel utilisateur</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Créez un compte sans passer par Shopify (promo, test, manuel)
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="username">Nom d&apos;utilisateur *</Label>
                    <Input
                      data-testid="username-input"
                      id="username"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="jean.dupont"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      data-testid="email-input"
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="jean@example.com"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="account_type">Type de compte</Label>
                    <Select 
                      value={formData.account_type} 
                      onValueChange={(value) => setFormData({ ...formData, account_type: value })}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="manual" className="text-white hover:bg-gray-700">
                          <span className="flex items-center gap-2">
                            <UserPlus className="w-4 h-4" /> Manuel
                          </span>
                        </SelectItem>
                        <SelectItem value="promo" className="text-white hover:bg-gray-700">
                          <span className="flex items-center gap-2">
                            <Gift className="w-4 h-4 text-amber-400" /> Promotionnel
                          </span>
                        </SelectItem>
                        <SelectItem value="test" className="text-white hover:bg-gray-700">
                          <span className="flex items-center gap-2">
                            <TestTube className="w-4 h-4 text-purple-400" /> Test
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="access_days">Durée d&apos;accès (jours)</Label>
                    <Input
                      id="access_days"
                      type="number"
                      min="0"
                      value={formData.access_days}
                      onChange={(e) => setFormData({ ...formData, access_days: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Illimité si vide"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="password">Mot de passe (optionnel)</Label>
                  <Input
                    id="password"
                    type="text"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="Généré auto si vide"
                  />
                </div>
                
                <div>
                  <Label htmlFor="notes">Notes admin (optionnel)</Label>
                  <Input
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="Ex: Client VIP, Partenaire..."
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="force_password_change"
                    checked={formData.force_password_change}
                    onCheckedChange={(checked) => setFormData({ ...formData, force_password_change: checked })}
                  />
                  <Label htmlFor="force_password_change" className="text-sm cursor-pointer">
                    Forcer le changement de mot de passe à la première connexion
                  </Label>
                </div>
                
                <Button
                  data-testid="submit-user-btn"
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Créer l&apos;utilisateur
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs and Export */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2">
            <Button
              onClick={() => setActiveTab('active')}
              variant={activeTab === 'active' ? 'default' : 'outline'}
              className={activeTab === 'active' 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800'}
            >
              <Users className="w-4 h-4 mr-2" />
              Actifs ({activeUsers.length})
            </Button>
            <Button
              onClick={() => setActiveTab('inactive')}
              variant={activeTab === 'inactive' ? 'default' : 'outline'}
              className={activeTab === 'inactive' 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800'}
            >
              <UserX className="w-4 h-4 mr-2" />
              Inactifs ({inactiveUsers.length})
            </Button>
          </div>
          
          {/* Export Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleExportExcel}
              variant="outline"
              size="sm"
              className="bg-transparent border-green-700 text-green-400 hover:bg-green-600/20"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button
              onClick={handleExportPDF}
              variant="outline"
              size="sm"
              className="bg-transparent border-red-700 text-red-400 hover:bg-red-600/20"
            >
              <FileText className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : displayedUsers.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center border border-gray-800">
            {activeTab === 'active' ? (
              <>
                <Users className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Aucun utilisateur actif</p>
              </>
            ) : (
              <>
                <UserX className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Aucun utilisateur inactif</p>
                <p className="text-gray-500 text-sm mt-2">Les comptes désactivés ou révoqués apparaîtront ici</p>
              </>
            )}
          </div>
        ) : (
          <div className="glass rounded-2xl p-6 border border-gray-800">
            <div className="space-y-3">
              {displayedUsers.map((user) => (
                <div
                  key={user.id}
                  data-testid={`user-card-${user.username}`}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    user.is_active 
                      ? 'bg-gray-900/30 border-gray-800' 
                      : 'bg-red-900/10 border-red-900/30'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-lg">{user.username}</h3>
                      {user.role === 'admin' && (
                        <span className="px-2 py-1 text-xs rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
                          Admin
                        </span>
                      )}
                      {!user.is_active && (
                        <span className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-1">
                          <Ban className="w-3 h-3" />
                          Désactivé
                        </span>
                      )}
                      {/* Account type badge */}
                      {user.account_type === 'promo' && (
                        <span className="px-2 py-1 text-xs rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-1">
                          <Gift className="w-3 h-3" />
                          Promo
                        </span>
                      )}
                      {user.account_type === 'test' && (
                        <span className="px-2 py-1 text-xs rounded bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center gap-1">
                          <TestTube className="w-3 h-3" />
                          Test
                        </span>
                      )}
                      {user.account_type === 'manual' && (
                        <span className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1">
                          <UserPlus className="w-3 h-3" />
                          Manuel
                        </span>
                      )}
                      {/* Expiration badge */}
                      {user.expires_at && (
                        <span className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                          new Date(user.expires_at) < new Date() 
                            ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                            : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                        }`}>
                          <Clock className="w-3 h-3" />
                          {new Date(user.expires_at) < new Date() 
                            ? 'Expiré' 
                            : `Exp: ${new Date(user.expires_at).toLocaleDateString('fr-FR')}`}
                        </span>
                      )}
                      {/* Legacy subscription plan (for Shopify users) */}
                      {user.subscription_plan && !user.account_type && (
                        <span className="px-2 py-1 text-xs rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                          {user.subscription_plan}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">{user.email}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                      <span>Créé le {new Date(user.created_at).toLocaleDateString('fr-FR')}</span>
                      {user.created_by && (
                        <span>Par: {user.created_by}</span>
                      )}
                      {user.admin_notes && (
                        <span className="text-cyan-400" title={user.admin_notes}>
                          📝 {user.admin_notes.substring(0, 30)}{user.admin_notes.length > 30 ? '...' : ''}
                        </span>
                      )}
                      {user.deactivated_reason && (
                        <span className="text-red-400">
                          Raison: {user.deactivated_reason}
                        </span>
                      )}
                      {user.activation_code && (
                        <span className="text-gray-500">
                          Code: {user.activation_code}
                        </span>
                      )}
                    </div>
                  </div>
                  {user.username !== currentUser?.username && (
                    <div className="flex items-center gap-2">
                      <Button
                        data-testid={`reset-password-${user.username}`}
                        onClick={() => handleResetPassword(user.username)}
                        size="sm"
                        className="bg-yellow-600/20 hover:bg-yellow-600 text-yellow-400 hover:text-white"
                        title="Réinitialiser le mot de passe"
                      >
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`toggle-status-${user.username}`}
                        onClick={() => handleToggleStatus(user.username)}
                        size="sm"
                        className={user.is_active ? 'bg-orange-600/20 hover:bg-orange-600 text-orange-400 hover:text-white' : 'bg-green-600/20 hover:bg-green-600 text-green-400 hover:text-white'}
                        title={user.is_active ? 'Désactiver' : 'Réactiver'}
                      >
                        {user.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </Button>
                      <Button
                        data-testid={`delete-user-${user.username}`}
                        onClick={() => handleDeleteUser(user.username)}
                        size="sm"
                        variant="destructive"
                        className="bg-red-600/20 hover:bg-red-600"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Temporary Password Dialog */}
      <Dialog open={tempPasswordDialog.open} onOpenChange={(open) => setTempPasswordDialog({ ...tempPasswordDialog, open })}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>✅ Utilisateur créé avec succès</DialogTitle>
            <DialogDescription className="text-gray-400">
              Partagez ces informations avec l&apos;utilisateur de manière sécurisée
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-yellow-600/10 border border-yellow-600/30">
              <p className="text-sm text-gray-400 mb-2">Nom d&apos;utilisateur</p>
              <div className="flex items-center justify-between">
                <p className="font-mono text-lg">{tempPasswordDialog.username}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(tempPasswordDialog.username)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-green-600/10 border border-green-600/30">
              <p className="text-sm text-gray-400 mb-2">Mot de passe</p>
              <div className="flex items-center justify-between">
                <p className="font-mono text-lg">{tempPasswordDialog.password}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(tempPasswordDialog.password)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {tempPasswordDialog.account_type && (
              <div className="p-4 rounded-lg bg-blue-600/10 border border-blue-600/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Type de compte</p>
                    <p className="font-semibold capitalize flex items-center gap-2">
                      {tempPasswordDialog.account_type === 'promo' && <Gift className="w-4 h-4 text-amber-400" />}
                      {tempPasswordDialog.account_type === 'test' && <TestTube className="w-4 h-4 text-purple-400" />}
                      {tempPasswordDialog.account_type === 'manual' && <UserPlus className="w-4 h-4 text-blue-400" />}
                      {tempPasswordDialog.account_type}
                    </p>
                  </div>
                  {tempPasswordDialog.expires_at && (
                    <div className="text-right">
                      <p className="text-sm text-gray-400 mb-1">Expire le</p>
                      <p className="font-semibold flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-cyan-400" />
                        {new Date(tempPasswordDialog.expires_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            <p className="text-sm text-yellow-400">
              ⚠️ L&apos;utilisateur devra peut-être changer ce mot de passe à sa première connexion
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
